/**
 * intake-custom.component.ts
 * Copyright © 2016-2018, My Coverage Plan, Inc.
 * All rights reserved.
 */

/**
 * @author Todd L Smith <tsmith@safetyweb.org>
 * @author Taylor DH Smith <tdsmith@safetyweb.org>
 */

import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild }
  from "@angular/core";
import { AbstractControl, FormGroup, FormControl } from "@angular/forms";
import {
  ActivatedRoute,
  Event as RouterEvent,
  NavigationEnd,
  Router,
  UrlSegment
} from "@angular/router";
import { DialogRef, ModalComponent } from "angular2-modal";
import { Modal } from "angular2-modal/plugins/bootstrap";
import { Observable, Subscription } from "rxjs/Rx";

import { UserService } from "../auth/identity/user.service";
import { AlertService } from "../common/alert.service";
import { ReferSubjectContextRequest }
  from "../server/messages/context-management";
import { AcknowledgmentResponse, GENERIC_REASON_TEXT, Message, ResponseCode } from "../server/messages/general";
import { MessagingService } from "../server/messaging.service";
import { SubjectService } from "../subjects/subject.service";
import { ChatPanelComponent } from "./chat-panel.component";
import { CommentService } from "./comment.service";
import { ExportModalComponent } from "./export-modal.component";
import { ReferralModalComponent }
  from "./referral-modal.component";
import { SaveableForm } from "./saveable-form";

import { AlgorithmService } from "../algorithms/management/algorithm.service";
import { IntakeStepService }
  from "../algorithms/management/intake-step.service";
import { Algorithm, AlgorithmType } from "../algorithms/structure/algorithm";
import { RunningAlgorithm } from "../algorithms/structure/running-algorithm";
import { Subject } from "../subjects/subject";
import { SubjectReference } from "../subjects/subject-reference";
import { ModalHelpers } from "../support/utilities";
import { oneline, textualList } from "../support/utilities/functions";
import { anyChildHasError, CustomValidators } from "../support/validators";
import { WatchlistService } from "../watchlist/watchlist.service";
import { AnswerProxy } from "../algorithms/management/answer-proxy";
import { IntakeStep, ReportStep } from "../algorithms/structure";
import { SettingService } from "app/configuration/setting.service";
import { ReportStepService } from "app/algorithms/management/report-step.service";
import { SearchService, Result } from "app/search/search.service";
import { ModalService, ConfirmationContext } from "app/common/modal.service";
import { AddReminderModalComponent, ReminderModalResult } from "app/watchlist/add-reminder-modal.component";
import { BuildFlags } from "app/configuration/build-flags";
import { DomSanitizer, SafeResourceUrl, SafeUrl } from "@angular/platform-browser";
import { TypedDataHolder } from "app/common/data/typed-data";
import { Visibility } from "./comment";

declare const window : Window &
  {requestIdleCallback : (cb : () => void) => number};

interface PMM {
  _: 'init' | 'log' | 'ACK' | 'FAIL' | 'submitAnswers' | 'sendReferral' | 'restart';
  __?: number;
}
interface PMInit extends PMM {
  _: 'init';
  __: number;
  appInfo: {
    name: string;
    flavor: string;
    version: number;
  };
}

interface Profile {
  name: string;
  flavors: string[];
  minimumVersion: number;
  algorithms: string[];
  mappingFunction: (name: string, data: any) => {algName: string, algData: TypedDataHolder}
}
const ALLOWED_PROFILE_NAMES : string[] = ["firstcheck"];
const KNOWN_PROFILES : {[name: string]: Profile} = {
  firstcheck: {
    name: "firstcheck",
    flavors: ["fc"],
    minimumVersion: 0,
    algorithms: ["a-administration"],
    mappingFunction: () => null
  }
}

const BROWSER_SAVE_NEEDED_MESSAGE : string = `
  It looks like you may have entered data since the last save. (This could
  include changes you have made to comments.)

  Are you sure you want to leave?
`;

const REQUIRED_STEPS_MODAL : ConfirmationContext = {
  title: "Required steps not complete",
  body: `You must fill out all of the required steps before saving!`
};

const MAG_ICON : string =
  `<span class="glyphicon glyphicon-search"><span>`;

const RETRIEVABILITY_MODAL : ConfirmationContext = {
  title: "Subject would not be retrievable!",
  body: `Since you haven't completed enough of the search index steps
        (marked with a <kbd>${MAG_ICON}</kbd>), this record would not be
        retrievable through all searches. Would you like to save anyway?`
};

const ORG_FLOW_URL: Map<string, string> = new Map([
    ["CRC-West", "/static/custom-flows/crcwest/"]
]);
const DEFAULT_URL = "/static/custom-flows/fcwifc/";

@Component({
  selector: "coverage-form",
  template: `
    <iframe style="height: max(calc(100vh - 163px), 800px); width: 100vw; border: none;" [src]="formTemplateURL" #intakeFrame></iframe>
`,
  styles: [],
  providers: []
})
export class CustomIntakeComponent implements OnInit, OnDestroy
{
  // TODO: this is a bad approach
  public unsafeURLBasis = this.users.self.groups.map(g => ORG_FLOW_URL.get(g.name)).find(x => !!x) || DEFAULT_URL;
  public formTemplateURL : SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    BuildFlags.useProductionHostValues ? this.unsafeURLBasis :  "http://localhost:4200"
  );

  public objectId : number = -1;

  public title : string;

  public algorithm : RunningAlgorithm;

  private paramNavSub : Subscription = new Subscription();
  private paramNavWindow : number = 1000;

  @ViewChild('intakeFrame')
  protected intakeFrame? : ElementRef;

  /**
   * Construct a new {@link CustomIntakeComponent}.
   */
  public constructor (
    private router : Router,
    private route : ActivatedRoute,
    private messaging : MessagingService,
    public subjects : SubjectService,
    private intakeSteps : IntakeStepService,
    private reportSteps : ReportStepService,
    private comments : CommentService,
    private alerts : AlertService,
    private modals : ModalService,
    private watchlist : WatchlistService,
    public algorithms : AlgorithmService,
    private users : UserService,
    private element : ElementRef,
    private settings : SettingService,
    private search : SearchService,
    private sanitizer : DomSanitizer
  )
  {
    // TODO: If we switch to allowing a no-interstitial-page transition, we'll
    // need a different event model (or router changes) so that IFC→IFC triggers
    // correct initialization. Subscribing to params alone does NOT suffice
    // because param changes occur before navigation success (so they could
    // represent a navigation doomed to fail).
    this.route.params.subscribe((params : any) => {
      if (this.objectId === undefined || this.objectId < 0) {
        // first run or run with dead id
        return;
      }
      // The long note above is from the non-custom flow. For the custom flow,
      // we do accept a changing-param CIC->CIC navigation, but we use it as
      // a signal to simply reload the inner app.
      this.sendPM({_: "restart"});

      // This version does not work for cross-origin security
      /*
      if (this.intakeFrame) {
        try
        {
          (this.intakeFrame.nativeElement as HTMLIFrameElement).contentWindow.location.reload();
        } catch (e) {
          console.error(`Could not find inner app for reloading: ${e}`);
        }
      }
        */
    });
  }

  public lockable () : true {return true;}

  public ngOnInit () : void
  {
    // this.algorithms.lock(this);
    this.algorithm = this.algorithms.running;
    this.title =
      this.algorithm.displayName || "Intake Form";

    // until we have ways to suppress by source
    this.alerts.suppressedLevels.add("notification");
    this.alerts.suppressedLevels.add("user");
    this.alerts.suppressedLevels.add("success");

    // this.context.registerManager(this);
    this.objectId = this.algorithms.running.subject.id;

    // this.resummarizeIfWatched();

    // TODO: Might be better way to ensure this reset?
    window.scrollTo(0, 0);

    console.log(this.users.self.groups.map(g => g.name).join(", ") + " ... " + this.unsafeURLBasis);
  }

  public ngOnDestroy () : void
  {
  }

  /**
   * Determine if the UI components are ready to be destroyed or not, generally
   * based on a user confirmation of their dirtyness / lost data.
   * @return {Observable<boolean>}
   */
  public canLeave () : Observable<boolean>
  {
    // FUTURE: this should use a postmessage to allow the inner to verify
    // its own readiness to leave
    return Observable.of(true);
  }

  /**
   * Determine if the algorithm's required steps have data.
   * @return {boolean}
   */
  private requiredStepsComplete () : boolean
  {
    return true;
  }

  public saveRequested : boolean = false;
  public saveRequestTime : number = -1;

  /**
   * Attempt a user-prompted save operation
   * @return {Observable<boolean>}
   */
  // public trySaveSubject (depart : boolean = false) : Observable<boolean>
  // {
  //   if (!this.requiredStepsComplete())
  //   {
  //     // The required dialog is just a warning and doesn't provide any
  //     // immediate action, so we can simply return the ready state and always
  //     // fail an attempted save when the required fields are missing.
  //     this.modals.alert(REQUIRED_STEPS_MODAL);
  //     return Observable.empty<boolean>();
  //   }
  //   this.saveRequested = true;
  //   this.saveRequestTime = Date.now();
  //   const saveResult : Observable<boolean> = this.saveSubject();
  //   saveResult.subscribe((saved : boolean) => {
  //     if (saved)
  //     {
  //       // this.form.markAsPristine();
  //       this.comments.resetChangeFlag();
  //       this.alerts.success("The record has been saved.");
  //       this.saveRequested = false;
  //       if (depart)
  //       {
  //         this.router.navigate(["/home"]);
  //       }
  //     }
  //   });
  //   return saveResult;
  // }

  /**
   * Ask the subject context to save itself (by communicating with the server).
   * @return {Observable<boolean>}
   */
  private saveSubject (force : boolean = false) : Observable<boolean>
  {
    return this.algorithm.save(force).flatMap(
      (code : ResponseCode) => {
        if (code === ResponseCode.OK)
        {
          // const ref : SubjectReference =
          //   this.algorithm.subject.findRef("watch");
          // if (!ref)
          // {
          //   const autoWatch : boolean =
          //     this.settings.get("subscribe after save");
          //   if (autoWatch)
          //   {
          //     this.watchlist.watchRunningSubject().subscribe();
          //   }

          // }
          // else
          // {
          //   this.resummarize(ref.id);
          // }
          return [true];
        }
        else if (code === ResponseCode.Irretrievable)
        {
          return [false];

          this.modals.confirm(RETRIEVABILITY_MODAL)
            .flatMap((saveHard : boolean) => {
              if (saveHard)
              {
                // Note that we are recursing here, so we're reliant upon the
                // fact that the server should NEVER fail with Irretrievable
                // after a forcible save to prevent a loop.
                return this.saveSubject(true);
              }
              return [false];
          });
        }
        else
        {
          this.alerts.warn(
            "The subject could not be saved: " + GENERIC_REASON_TEXT.get(code));
          return [false];
        }
      }
    );
  }

  // public tryDuplicate () : void
  // {
  //   this.subjects.launchDuplicate(this.algorithm.algorithm);
  // }

  private resummarize (watchId : number) : void
  {
    this.watchlist.resummarize({watchId}).subscribe();
  }

  private resummarizeIfWatched () : void
  {
    // TODO: revisit this flow. Can subjects resummarize themselves?
    const ref : {id : number} =
      this.algorithm.subject.findRef("watch");
    if (ref)
    {
      this.resummarize(ref.id);
    }
  }

  /**
   * Unload is run before the page is closed and checks if there are any unsaved
   * changes, triggering a browser-controlled stay/leave dialog if so.
   * @param  {BeforeUnloadEvent} event
   */
  @HostListener("window:beforeunload", ["$event"])
  public unload (event : BeforeUnloadEvent) : void
  {
    // FUTURE: we can't do async operations here but we could have the inner
    // frame declare its ready-to-leave status periodically
    if (false)
    {
      // most browsers ignore this message nowadays (due to the prevalence of
      // social engineering attacks on tab closure), but it's still part of the
      // spec and could return one day *shrug*
      event.returnValue = BROWSER_SAVE_NEEDED_MESSAGE;
      return;
    }
  }
  // TODO: This manual animation might get a small performance boost from being
  // moved outside of the Angular zone system? (though it's risky for errors)

  public save () : Observable<boolean>
  {
    throw new Error("UNIMPLEMENTED - PARTIAL INTERFACE COMPLIANCES");
  }

  public showLinkedReminderModal () : void
  {
    this.modals.observe<ReminderModalResult>(
      AddReminderModalComponent,
      {size: "md", hasImpliedLink: true}
    ).subscribe((r : ReminderModalResult | null) => {
      if (r)
      {
        this.watchlist.addReminder(
          r.summary,
          r.triggerTime,
          r.linkedRecord ? this.objectId : undefined
        ).subscribe();
      }
    });
  }

  public submitFromChild (
    rawAnswers : Array<[string, any]>,
    comments: Array<[string, any]>
  ) {
    const stepAnswers = rawAnswers.map(([key, value]) => {
      const step = this.intakeSteps.findNamed(key);
      if (!step) {
        console.warn(`Couldn't find step named "${key}", skipping`);
        return null;
      }
      if ((step.cardinality === 1) === Array.isArray(value)) {
        debugger;
        console.error(`Cardinality mismatch, ${step.name} expected ${step.cardinality === 1 ? "scalar" : "array"} value, got ${value}`);
        // return null;
      }
      // TODO: other transforms?
      switch (step.type) {
        case "date":
          if (step.cardinality !== 1) {
            console.error("Lists of dates not yet convertible");
            return null;
          }
          if (Number.isSafeInteger(value)) {
            step.value = new Date(value);
          } else {
            return null;
          }
          break;
        case "integer":
        case "natural number":
        case "whole number":
          if (!Number.isSafeInteger(value)) {
            console.error(`Integer-like step got a non-integer value, skipping!\n  ${step.name} = ${value}`);
            return null;
          }
        default:
          step.value = value;
      }
      return step.completedIntakeStep();
    }).filter(i => i);
    try {
      const j = JSON.stringify(stepAnswers);
    } catch (err) {
      console.error(err);
    }
    const o : Observable<boolean> = this.intakeSteps.submit(stepAnswers).map((m : AcknowledgmentResponse) => !!m.ok);
    o.subscribe();

    const allObs : Observable<boolean>[] = [o];
    
    comments.forEach(
      ([stepKey, commentText]) => 
        allObs.push(
          this.comments.addForStep(
            this.intakeSteps.findNamed(stepKey).id,
            commentText,
            Visibility.Public)));
    return Observable.forkJoin(...allObs);
  }

  // public openReferralModal () : void
  // {
  //   this.modals.observe(ReferralModalComponent)
  //     .flatMap((referralMsg : string) => {
  //       if (!referralMsg)
  //       {
  //         return [false];
  //       }
  //       return this.messaging.resultOf(new ReferSubjectContextRequest(
  //         this.algorithm.subject.id,
  //         referralMsg
  //       ));
  //     }).subscribe((referred : boolean) => {
  //       if (referred)
  //       {
  //         this.alerts.success("The subject has been referred!");
  //       }
  //     });
  // }

  
  // @HostListener("document:keydown.control.s", ["$event"])
  // @HostListener("document:keydown.meta.s", ["$event"])
  // public saveFromKeyPress (
  //   ev : Event & {submitFirst? : [AnswerProxy, IntakeStep]}
  // ) : void
  // {
  //   if (document.activeElement instanceof HTMLElement)
  //   {
  //     document.activeElement.blur();
  //     requestAnimationFrame(() => this.trySaveSubject());
  //   }
  //   else
  //   {
  //     this.trySaveSubject();
  //   }
  //   ev.preventDefault();
  // }

  public scrollToTop () : void
  {
    document.scrollingElement.scrollTo({top: 0});
  }

  public printPage () : void
  {
    console.log(window);
    window.print();
  }

  protected outgoingId = -1;
  protected sendPM (m : PMM) {
    if (!('__' in m)) {
      m.__ = this.outgoingId--;
    }
    if (!this.intakeFrame || !this.intakeFrame.nativeElement || this.intakeFrame.nativeElement.tagName !== "IFRAME") {
      console.error("Cannot find frame element!");
      return;
    }
    const frameWindow = (this.intakeFrame.nativeElement as HTMLIFrameElement).contentWindow;
    frameWindow.postMessage(m, BuildFlags.useProductionHostValues ? location.origin :  "http://localhost:4200");
  }
  protected sendAck (incoming : PMM) {
    this.sendPM({_: "ACK", __: incoming.__});
  }
  protected sendFail (incoming : PMM) {
    this.sendPM({_: "FAIL", __: incoming.__});
  }

  protected incomingAppInfo : any = {name: "N/A"};
  protected appProfile : Profile;
  protected pmRecordStart (e : PMInit) {
    this.incomingAppInfo = e.appInfo;
    if (!ALLOWED_PROFILE_NAMES.includes(e.appInfo.name))
    {
      console.error(`Could not find profile for custom intake ${e.appInfo.name}`);
      return this.sendFail(e);
    }
    const profile = KNOWN_PROFILES[e.appInfo.name];
    if (profile.minimumVersion > e.appInfo.version 
      || !profile.flavors.includes(e.appInfo.flavor))
    {
      console.error(`Custom intake ${e.appInfo.name} does not support flavor+version ${e.appInfo.flavor}v${e.appInfo.version} (needs [${profile.flavors.join(",")}], ≥v${profile.minimumVersion})`,);
      return this.sendFail(e);
    }
    if (!profile.algorithms.includes(this.algorithm.name)) {
      console.error(`Profile ${profile.name} is not compatible with algorithm ${this.algorithm.name}!`);
      return this.sendFail(e);
    }
    console.info("Found matching profile, acknowledging");
    this.appProfile = profile;
    this.sendAck(e);
  }

  @HostListener("window:message", ["$event"])
  protected incomingPMListener (e : MessageEvent) : void
  {
    if (BuildFlags.useProductionHostValues ? 
      (e.origin === location.origin) :
      (e.origin === "http://localhost:4200"))
    {
      if (!e.data || typeof e.data !== 'object' || typeof e.data._ !== 'string') {
        console.error(`Received non-object msg, or msg without a _ type`, e.data);
        return;
      }
      console.info(`Attempting to handle ${e.data._}...`, e.data);
      switch ((e.data as PMM)._) {
        case "init":
          this.pmRecordStart(e.data);
          return;
        case "log":
          let verb : "info" | "log" | "warn" | "error" = 
            (["info", "log", "warn", "error"].includes(e.data.level))
            ? e.data.level : "log";
          console[verb]("Child application requested logging:", e.data.message);
          return;
        case "submitAnswers":
          this.submitFromChild(e.data.answers || [], e.data.comments || []).flatMap((successes : boolean[]) => {
            debugger;
            if (successes.every(s => s)) {
              return this.saveSubject(true)
            }
            return [false];
          }).flatMap((saved : boolean) => {
            debugger;
            if (saved) {
              return this.messaging.resultOf(new ReferSubjectContextRequest(
                this.algorithm.subject.id,
                `Automated FCWI referral at ${(new Date()).toLocaleString()}`
              ));
            }
            return [saved];
          }).subscribe(success => {
            debugger;
            if (success) {
              this.sendAck(e.data);
            } else {
              this.sendFail(e.data);
            }
          }, err => {
            this.sendFail(e.data);
          })
          return;
        case "restart":
          this.subjects.launchNew(this.algorithm.algorithm);
          return;
        default:
          console.warn(`Unhandled PM type ${e.data._}, ignoring`, e.data);
          this.sendFail(e.data);
      }
    } else {
      console.error(`Rejecting insecure message from ${e.origin}`, e);
    }
  }
}



// WEBPACK FOOTER //
// ./~/angular-router-loader/src!./app/intake-form/intake-custom.component.ts